import "../../css/FullEvent/FullEvent.css";
import "../../css/GlobalStyles.css";
import { memo, useMemo } from "react";
import {
  Event,
  AccountData,
  DiscussionPost,
  CustomTicketV2,
  PromoCode,
  TicketV2,
} from "@markit/common.types";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { TimeAgo } from "../../utils/TimeAgo";
import ProgressBar from "@ramonak/react-progress-bar";
import { isEventTicketsPaid } from "@markit/common.utils";
import { FullEventLocationPin } from "./FullEventLocationPin";
import { isDesktop } from "react-device-detect";
import { GuestListSummary } from "../FullEventSubComponents/GuestListSummary";
import { InitialsPic } from "../InitialsPic";
import Banner, { BannerType } from "../Banner";
import Footer from "../Footer";
import { useTheme } from "../../hooks/useTheme";
import FullEventHosts from "./FullEventHosts";
import { generateTextFromJSON } from "../../utils/stringUtils";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import FullEventInfoTickets from "./FullEventInfoTickets";
import ReadOnlyRichTextEditor from "../TextFields/ReadOnlyRichTextEditor";

const SECONDLINEPLEASURECLUB_UID = "5JG881ZI2rRE7XZYezHInxmGMtu2";

type FullEventInfoProps = {
  event: Event;
  host: AccountData | undefined;
  cohostsList?: AccountData[] | undefined;
  posts: DiscussionPost[];
  address: string;
  visibleTickets: CustomTicketV2[];
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (selectedCustomTickets: CustomTicketV2[]) => void;
  promoCode: PromoCode | undefined;
  redeemTicket: TicketV2 | undefined;
  setShowCheckoutForm: (showCheckoutForm: boolean) => void;
};

const FullEventInfo = memo(function FullEventInfoFn(props: FullEventInfoProps) {
  const {
    event,
    host,
    cohostsList,
    posts,
    address,
    visibleTickets,
    selectedCustomTickets,
    setSelectedCustomTickets,
    promoCode,
    redeemTicket,
    setShowCheckoutForm,
  } = props;
  const { theme } = useTheme();

  // Helps check if event body is empty or not
  const eventDescription = useMemo(
    () => generateTextFromJSON(event.body),
    [event.body]
  );

  const ticketsPaid = useMemo(() => isEventTicketsPaid(event), [event]);

  const checkCrowdfundingHide = useMemo(
    () => (event?.crowdfundingHide ? event?.crowdfundingHide : false),
    [event?.crowdfundingHide]
  );

  const hostAndCohosts: AccountData[] =
    cohostsList && host ? [host, ...cohostsList] : host ? [host] : [];

  const renderDivider = useMemo(
    () => (
      <HorizontalDivider altMargin={14} altWidth={1} eventTheme={event.theme} />
    ),
    [event.theme]
  );

  const renderDescription = useMemo(
    () =>
      eventDescription ? (
        <div className="ColumnNormal">
          <span className="bodySubtext" style={{ fontWeight: 600 }}>
            About this Event
          </span>
          {renderDivider}
          <ReadOnlyRichTextEditor
            body={event.body}
            theme={theme}
            className={`post-content-editor ${
              isDesktop ? "" : "mobile-editor"
            }`}
          />
        </div>
      ) : null,
    [event.body, eventDescription, renderDivider, theme]
  );

  return (
    <div className="ColumnNormal" style={{ gap: 30 }}>
      {/* TEMP: Change position of description for Second Line Pleasure Club */}
      {event.createdBy === SECONDLINEPLEASURECLUB_UID
        ? renderDescription
        : null}
      <FullEventInfoTickets
        event={event}
        visibleTickets={visibleTickets}
        selectedCustomTickets={selectedCustomTickets}
        setSelectedCustomTickets={setSelectedCustomTickets}
        promoCode={promoCode}
        redeemTicket={redeemTicket}
        setShowCheckoutForm={setShowCheckoutForm}
      />
      {event.createdBy !== SECONDLINEPLEASURECLUB_UID
        ? renderDescription
        : null}
      <GuestListSummary event={event} />
      <div className="ColumnNormal">
        <span className="bodySubtext" style={{ fontWeight: 600 }}>
          Location
        </span>
        {renderDivider}
        <FullEventLocationPin event={event} address={address} />
      </div>
      {ticketsPaid && !event.canRefund ? (
        <div className="ColumnNormal">
          <span className="bodySubtext" style={{ fontWeight: 600 }}>
            Refund Policy
          </span>
          {renderDivider}
          <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
            No refunds
          </span>
        </div>
      ) : null}
      {!isDesktop ? (
        <div className="ColumnNormal">
          <span className="bodySubtext" style={{ fontWeight: 600 }}>
            Hosted by
          </span>
          {renderDivider}
          <FullEventHosts host={host} hostAndCohosts={hostAndCohosts} />
          <div style={{ marginTop: "14px" }}>
            {host && host.customer.phone !== "" ? (
              <Banner
                type={BannerType.TEXTHOST}
                hostPhone={host.customer.phone}
              />
            ) : (
              <Banner type={BannerType.TEXTHOST_FREE} />
            )}
          </div>
        </div>
      ) : null}
      {event?.crowdfundingGoal &&
      event?.crowdfundingGoal !== 0 &&
      !checkCrowdfundingHide ? (
        <div>
          <div className="ResponseHeader">
            <h3 className="AboutSubtitle">Crowdfunding</h3>
            <p>
              <span className="CrowdfundingText">
                {FormatCurrency(
                  event?.contributions.reduce(
                    (partialSum, a) => partialSum + a.amount,
                    0
                  ) / 100
                )}
              </span>{" "}
              of {FormatCurrency(event?.crowdfundingGoal / 100)}
            </p>
          </div>
          <ProgressBar
            completed={
              event?.contributions.reduce(
                (partialSum, a) => partialSum + a.amount,
                0
              ) / 100
            }
            maxCompleted={event?.crowdfundingGoal / 100}
            isLabelVisible={false}
            bgColor="#11b8ff"
            baseBgColor="rgba(133,134,145,0.25)"
            height="10px"
            margin="0 0 1.5em 0"
          />
          <hr />
        </div>
      ) : null}
      {posts.length !== 0 ? (
        <>
          <div>
            <h3 className="AboutSubtitle">Posts ({posts.length})</h3>
            {posts.length > 0 &&
              posts.map((post, index) => (
                <div key={index} className="PostContainer">
                  {host?.profilePicURL && host?.profilePicURL !== "" ? (
                    <img
                      className="PostProfilePic"
                      src={host?.profilePicURL}
                      alt="profile pic"
                    />
                  ) : (
                    <div className="PostProfilePic InitialsPic">
                      <InitialsPic fullName={host?.fullName} />
                    </div>
                  )}
                  <div className="PostNameContainer">
                    <p className="FullNamePost">{host?.fullName}</p>
                    <p className="TimeAgoText" style={{ marginTop: 5 }}>
                      {TimeAgo(post.postedAt)}
                    </p>
                    <p className="PostText">{post.text}</p>
                  </div>
                </div>
              ))}
          </div>
          <hr style={{ marginBottom: 10, marginTop: 20 }} />
        </>
      ) : undefined}
      {!isDesktop ? (
        <div style={{ paddingTop: 30 }}>
          <Footer showRCA creatorData={host} />{" "}
        </div>
      ) : null}
    </div>
  );
});

export default FullEventInfo;
